import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button, Result, Spin } from 'antd';
import styled from 'styled-components';

import { axiosInt } from '../../utils/axios';
import useAuth from '../hooks/useAuth';
import { getRefreshClientId } from '../utils/refreshToken';

interface IRimsLoginProps {
  dashboardPath: string;
}

const exchangeRimsCode = (code: string) =>
  axiosInt
    // clientId ties the minted refresh token to this app's own OAuth2 client - without it the
    // backend defaults to react-admin-app, which later refresh calls (made as this app's own
    // client) would fail to use.
    .post('/ims/auth/rims/exchange', { code, clientId: getRefreshClientId() })
    .then(({ data }) => data);

const RimsLogin: React.FC<IRimsLoginProps> = ({ dashboardPath }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUserToken, token } = useAuth();

  const { mutate, isSuccess, isError } = useMutation({
    mutationFn: exchangeRimsCode,
    onSuccess: data => {
      setUserToken({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      });
      navigate(dashboardPath, { replace: true });
    },
  });

  useEffect(() => {
    if (token) {
      navigate(dashboardPath, { replace: true });
      return;
    }

    const code = searchParams.get('code');
    if (code && !token) mutate(code);
  }, []);

  if (isError) {
    return (
      <WrapperDiv>
        <Result
          status="error"
          title="Login error"
          subTitle="Failed to authenticate. Please try again."
          extra={[
            <Button
              type="primary"
              key="tryAgain"
              onClick={() => window.history.back()}
            >
              Try again
            </Button>,
          ]}
        />
      </WrapperDiv>
    );
  }

  return (
    <WrapperDiv>
      <Spin
        tip={isSuccess ? 'Redirecting...' : 'Finalizing login...'}
        size="large"
      />
    </WrapperDiv>
  );
};

export default RimsLogin;

const WrapperDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 90vh;
  background-color: #f5f7f9;
  width: 100%;
`;
